'use client';

import copy from 'copy-to-clipboard';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Copy, Linkedin, Telegram, XIcon } from '@zoomit/icons';
import type { PopoverPlacement} from '@zoomit/primitives';
import { Box, Flex, IconButton, Popover, useHideAfterXSecond } from '@zoomit/primitives';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

type Props = {
  url: string;
  title: string;
  offset?: number;
  placement: PopoverPlacement;
  showShare: boolean;
  setShowShare: (show: boolean) => void;
  onShare?: (method: string) => void;
  children: ReactNode;
};

export const SharePopover = (props: Props) => {
  const { showShare, setShowShare, children, url, onShare, title, placement, offset } = props;

  const { onStart, state: isCopied } = useHideAfterXSecond({ seconds: 2 });

  const copyHandler = () => {
    copy(url);
    onStart();
  };

  const socialMedias = [
    {
      name: 'telegram',
      icon: <Telegram />,
      link: `https://telegram.me/share/?url=${url}`,
    },
    {
      name: 'twitter',
      link: `https://twitter.com/share?via=TheZoomit&text=${title}&url=${url}`,
      icon: <XIcon />,
    },
    {
      name: 'linkedin',
      icon: <Linkedin />,
      link: `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
    },
  ];

  return (
    <Popover
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      changingIsOpenWhileClicking={false}
      isOpen={showShare}
      setOpen={setShowShare}
      placement={placement}
      triggerOffset={offset}
      content={
        <Box
          className="h-[42px] w-[380px] rounded-md shadow-md"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          backgroundColor={cssVar(theme.card)}
        >
          <ShareBox justifyContent="space-between" alignItems="center">
            <Flex gap={1.6} spacing={{ mr: 1 }}>
              {socialMedias.map(({ name, link, icon }) => (
                <IconButton
                  key={name}
                  onClick={() => {
                    onShare?.(name);
                    window.open(link);
                  }}
                >
                  {icon}
                </IconButton>
              ))}
            </Flex>
            <Flex alignItems="center">
              {isCopied && (
                <Typography spacing={{ ml: 0.4 }} variant="subhead-bold" textColor={cssVar(theme.success1)}>
                  کپی شد
                </Typography>
              )}
              <LinkAddress
                dir="ltr"
                textColor={isCopied ? cssVar(theme.success1) : cssVar(theme.text)}
                spacing={{ ml: 0.4 }}
                variant="subhead-bold"
              >
                {url}
              </LinkAddress>
              <IconButton onClick={() => copyHandler()}>
                <Copy
                  className={'transition-all duration-150'}
                  fill={isCopied ? cssVar(theme.success1) : cssVar(theme.text)}
                />
              </IconButton>
            </Flex>
          </ShareBox>
        </Box>
      }
    >
      <Flex>{children}</Flex>
    </Popover>
  );
};

const ShareBox = styled(Flex)`
  width: 100%;
  height: 100%;
  position: relative;
  padding: 0 1.6rem;
  &::after {
    content: '';
    position: absolute;
    width: 4px;
    height: 2.4rem;
    right: 0;
    z-index: 2;
    background-color: ${cssVar(theme.primary1)};
    border-radius: var(${theme.radius.sm});
  }
`;

const LinkAddress = styled(Typography)`
  white-space: nowrap;
  width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: 0.15s;
`;
